import { attributes } from '../../core/ids-attributes';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';
import { IdsConstructor } from '../../core/ids-element';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

import '../../components/ids-tooltip/ids-tooltip';
import type IdsTooltip from '../../components/ids-tooltip/ids-tooltip';
import type { IdsTooltipPlacement } from '../../components/ids-tooltip/ids-tooltip';
import { isValidBooleanValue, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

interface TooltipMixinInterface {
  canTooltipShow?(tooltipEl: IdsTooltip | null, tooltipContent: string): boolean;
  onTooltipTargetDetection?(tooltipEl: IdsTooltip | null): HTMLElement | SVGElement;
  getTooltipContent?(): string;
}

type Constraints = IdsConstructor<EventsMixinInterface & TooltipMixinInterface>;

/**
/**
 * A mixin that adds tooltip functionality to components
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsTooltipMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.TOOLTIP,
      attributes.TOOLTIP_PLACEMENT,
      attributes.TOOLTIP_PLAIN_TEXT,
      attributes.OVERFLOW_TOOLTIP,
    ];
  }

  connectedCallback() {
    super.connectedCallback?.();
    this.handleTooltipEvents();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
  }

  /**
   * Init the mixin events and states
   * @private
   */
  handleTooltipEvents() {
    this.offEvent('hoverend.tooltipmixin');
    const hasOverflowTooltip = this.hasAttribute(attributes.OVERFLOW_TOOLTIP);
    if (!this.tooltip && !hasOverflowTooltip) return;

    // Use the tooltip target element for hover events instead of the component itself
    const target = this?.toolTipTarget || this;
    this.onEvent('hoverend.tooltipmixin', target, (e: CustomEvent) => {
      e.stopPropagation();
      this.showTooltip();
    }, { delay: 500, bubbles: true });
  }

  /**
   * @returns {IdsTooltip | null} reference to an established IdsTooltip
   */
  get tooltipEl(): IdsTooltip | null {
    return this.getTooltipContainer()?.querySelector<IdsTooltip>('#mixin-tooltip');
  }

  /**
   * Return the correct target element
   * @private
   * @returns {HTMLElement | SVGElement} The correct target element
   */
  get toolTipTarget() {
    let target: HTMLElement | SVGElement = this;

    if (typeof this.onTooltipTargetDetection === 'function') {
      target = this.onTooltipTargetDetection(this.tooltipEl);
    }

    return target;
  }

  /**
   * @returns {HTMLElement} reference to the correct container element in which to find the mixin tooltip.
   */
  getTooltipContainer() {
    let container = this.closest('ids-container');
    if (!container) {
      container = document.body;
    }
    return container;
  }

  /**
   * Show the tooltip if available
   */
  showTooltip() {
    const hasCustomTooltip = this.tooltip && this.tooltip !== 'true' && this.tooltip !== 'false';

    // For ellipsis tooltip check if overflowing and only show if it is
    if (!hasCustomTooltip && typeof this.canTooltipShow === 'function') {
      if (!this.canTooltipShow(this.tooltipEl, this.tooltip)) return;
    }

    if (isValidBooleanValue(this.tooltip) && stringToBool(this.tooltip) === false) {
      // Allow overflow-tooltip to bypass this check
      if (!this.hasAttribute(attributes.OVERFLOW_TOOLTIP)) return;
    }

    // Locate the tooltip's container
    const container = this.getTooltipContainer();

    // Generate a tooltip if needed, or query an existing one
    let tooltip = container.querySelector<IdsTooltip>('#mixin-tooltip') || document.querySelector('#mixin-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('ids-tooltip') as IdsTooltip;
      tooltip.id = 'mixin-tooltip';
      container?.appendChild(tooltip);
    }

    if (tooltip.visible && tooltip.target && tooltip.target !== this.toolTipTarget) {
      const currentTarget = tooltip.target as HTMLElement;
      if (this.contains && this.contains(currentTarget)) {
        // The tooltip is already showing for a child element, don't override
        return;
      }
    }

    if (!tooltip.state) {
      tooltip.state = {};
    }
    tooltip.target = this.toolTipTarget;
    tooltip.state.noAria = true;

    // Ensure the popup is aligned to the correct target
    if (tooltip.popup) {
      tooltip.popup.alignTarget = this.toolTipTarget;
    }

    // Handle Ellipsis Text if tooltip="true"
    try {
      if (typeof this.getTooltipContent === 'function') {
        tooltip.textContent = this.getTooltipContent();
      } else if (this.hasAttribute(attributes.TOOLTIP_PLAIN_TEXT)) {
        tooltip.textContent = this.tooltip === 'true' ? this.textContent : this.tooltip;
      } else {
        tooltip.innerHTML = this.tooltip === 'true' ? this.innerHTML : this.tooltip;
      }
    } catch (e) {
      tooltip.textContent = this.tooltip === 'true' ? this.textContent : this.tooltip;
    }

    if (this.hasAttribute(attributes.TOOLTIP_PLACEMENT)) {
      tooltip.placement = this.getAttribute(attributes.TOOLTIP_PLACEMENT) as IdsTooltipPlacement;
    }

    const rootNode = (this.getRootNode() as any).host;
    if (rootNode && rootNode.name === 'ids-tab' && rootNode.firstChild.wholeText !== '') {
      tooltip.innerHTML = rootNode.firstChild.wholeText;
    }

    // Show it
    tooltip.visible = true;
    tooltip.popup?.place();

    if (getClosest(this, 'ids-container')?.getAttribute('dir') === 'rtl') tooltip.popup?.setAttribute('dir', 'rtl');
    if (this.beforeTooltipShow) this.beforeTooltipShow(tooltip);

    // Remove it when closed
    tooltip.onEvent('hide.tooltipmixin', tooltip, () => {
      document.querySelector('#mixin-tooltip')?.remove();
    });
  }

  /**
   * Setup some special config for the tooltip
   * @param {any} tooltip The tooltip to configure
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  beforeTooltipShow(tooltip?: any) {
  }

  /**
   * Set the tooltip to a particular string
   * @param {string} value The tooltip value
   */
  set tooltip(value: string) {
    if (value) {
      this.setAttribute('tooltip', value);
      this.container?.setAttribute('tooltip', value);
      this.handleTooltipEvents();
    }
  }

  get tooltip() {
    return this.getAttribute('tooltip') as string;
  }

  /**
   * Sets which tooltip to show based on overflow state.
   * @param {string | null} value 'text' shows element text when overflowing,
   * 'custom' shows custom tooltip, 'false' disables overflow tooltip
   */
  set overflowTooltip(value: string | null) {
    if (value && ['text', 'custom', 'false'].includes(value)) {
      this.setAttribute(attributes.OVERFLOW_TOOLTIP, value);
    } else {
      this.removeAttribute(attributes.OVERFLOW_TOOLTIP);
    }
    this.handleTooltipEvents();
  }

  /**
   * Gets the overflow-tooltip attribute value.
   * @returns {string | null} 'text', 'custom', 'false', or null
   */
  get overflowTooltip(): string | null {
    return this.getAttribute(attributes.OVERFLOW_TOOLTIP);
  }
};

export default IdsTooltipMixin;
